/**
 * Centralized date formatting utilities for consistent backend communication
 */

/**
 * Day of week enum - normalized to 1-7 (Sunday to Saturday)
 */
export enum DayOfWeek {
  SUNDAY = 1,
  MONDAY = 2,
  TUESDAY = 3,
  WEDNESDAY = 4,
  THURSDAY = 5,
  FRIDAY = 6,
  SATURDAY = 7
}

/**
 * Converte datetime-local para ISO string para envio ao backend
 * Este é o formato padrão que deve ser usado em toda comunicação com a API
 * Formato: 2025-07-28T20:21:06Z
 * IMPORTANTE: Considera o timezone do usuário e converte corretamente para UTC
 * @param dateTimeString String no formato datetime-local (YYYY-MM-DDTHH:mm)
 * @returns String no formato ISO (YYYY-MM-DDTHH:mm:ssZ)
 */
export const formatDateTimeForAPI = (dateTimeString: string): string => {
  if (!dateTimeString) return new Date().toISOString()
  
  // datetime-local é interpretado como horário local do usuário
  const localDate = new Date(dateTimeString)
  
  // Converte para UTC considerando o timezone do usuário
  // Garante formato: 2025-07-28T20:21:06Z (sem milissegundos)
  return localDate.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Converte Date object para ISO string para envio ao backend
 * Formato: 2025-07-28T20:21:06Z
 * @param date Date object
 * @returns String no formato ISO (YYYY-MM-DDTHH:mm:ssZ)
 */
export const formatDateForAPI = (date: Date): string => {
  // Garante formato: 2025-07-28T20:21:06Z (sem milissegundos)
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Parse seguro de datas ISO vindas da API com tratamento robusto
 * @param dateString String de data vinda da API
 * @returns Date object válido
 */
export const parseAPIDate = (dateString: any): Date => {
  if (!dateString) {
    return new Date()
  }
  
  // Converte para string se não for
  const dateStr = String(dateString).trim()
  
  // Tenta parse direto primeiro
  const directParse = new Date(dateStr)
  if (!isNaN(directParse.getTime())) {
    return directParse
  }
  
  // Se falhar, tenta com ajustes
  let cleanDateString = dateStr
  
  // Se termina com Z, é UTC
  if (cleanDateString.endsWith('Z')) {
    const withZ = new Date(cleanDateString)
    if (!isNaN(withZ.getTime())) {
      return withZ
    }
  }
  
  // Se tem T mas não tem Z, adiciona Z para UTC
  if (cleanDateString.includes('T') && !cleanDateString.endsWith('Z')) {
    cleanDateString += 'Z'
    const withAddedZ = new Date(cleanDateString)
    if (!isNaN(withAddedZ.getTime())) {
      return withAddedZ
    }
  }
  
  console.warn('Failed to parse date:', dateString)
  return new Date() // Fallback
}

/**
 * Formata data para input datetime-local
 * @param date Date object
 * @returns String no formato datetime-local (YYYY-MM-DDTHH:mm)
 */
export const formatDateTimeForInput = (date: Date): string => {
  return date.toISOString().slice(0, 16)
}

/**
 * Normaliza horários em incrementos de 30 minutos para campos de input
 * @param dateTimeString String datetime-local
 * @returns String datetime-local normalizada para incrementos de 30min
 */
export const normalizeTimeStep = (dateTimeString: string): string => {
  try {
    if (!dateTimeString) return dateTimeString
    
    const date = new Date(dateTimeString)
    if (isNaN(date.getTime())) return dateTimeString
    
    const minutes = date.getMinutes()
    const snapped = minutes - (minutes % 30)
    date.setMinutes(snapped, 0, 0)
    
    return formatDateTimeForInput(date)
  } catch {
    return dateTimeString
  }
}

/**
 * Converte data para início do dia (00:00:00.000) no timezone local
 * @param date Date object ou string de data
 * @returns Date object com horário zerado no timezone local
 */
export const getStartOfDay = (date: Date | string): Date => {
  let baseDate: Date
  
  if (typeof date === 'string') {
    // Para strings de data (ex: "2025-07-28"), interpreta como data local
    baseDate = new Date(date + 'T00:00:00')
  } else {
    baseDate = new Date(date)
  }
  
  const startOfDay = new Date(baseDate)
  startOfDay.setHours(0, 0, 0, 0)
  return startOfDay
}

/**
 * Converte data para final do dia (23:59:59.999) no timezone local
 * @param date Date object ou string de data
 * @returns Date object com horário final no timezone local
 */
export const getEndOfDay = (date: Date | string): Date => {
  let baseDate: Date
  
  if (typeof date === 'string') {
    // Para strings de data (ex: "2025-07-28"), interpreta como data local
    baseDate = new Date(date + 'T23:59:59')
  } else {
    baseDate = new Date(date)
  }
  
  const endOfDay = new Date(baseDate)
  endOfDay.setHours(23, 59, 59, 999)
  return endOfDay
}

/**
 * Converte filtros de data para formato da API
 * IMPORTANTE: Considera o timezone do usuário e converte corretamente para UTC
 * @param filters Filtros do frontend
 * @returns Objeto com startDate e endDate em formato ISO
 */
export const mapDateFiltersToAPI = (filters: any): { startDate?: string; endDate?: string } => {
  const result: { startDate?: string; endDate?: string } = {}
  
  if (filters.dataPreset || filters.dataCustomizada) {
    const hoje = new Date()
    
    if (filters.dataPreset === 'hoje') {
      result.startDate = formatDateForAPI(getStartOfDay(hoje))
      result.endDate = formatDateForAPI(getEndOfDay(hoje))
    } else if (filters.dataPreset === 'amanha') {
      const amanha = new Date(hoje)
      amanha.setDate(hoje.getDate() + 1)
      result.startDate = formatDateForAPI(getStartOfDay(amanha))
      result.endDate = formatDateForAPI(getEndOfDay(amanha))
    } else if (filters.dataPreset === 'esta-semana') {
      // Início da semana (domingo) - no timezone local
      const startOfWeek = new Date(hoje)
      startOfWeek.setDate(hoje.getDate() - hoje.getDay())
      
      // Final da semana (sábado) - no timezone local
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)
      
      result.startDate = formatDateForAPI(getStartOfDay(startOfWeek))
      result.endDate = formatDateForAPI(getEndOfDay(endOfWeek))
    } else if (filters.dataPreset === 'customizada' && filters.dataCustomizada) {
      // Para data customizada, usa a data selecionada no timezone local
      // Se for string, passa direto para getStartOfDay/getEndOfDay que tratam corretamente
      result.startDate = formatDateForAPI(getStartOfDay(filters.dataCustomizada))
      result.endDate = formatDateForAPI(getEndOfDay(filters.dataCustomizada))
    }
  }
  
  return result
}

/**
 * Função de debug para testar conversões de timezone
 * Remove depois de testar
 */
export const debugTimezone = () => {
  const testDate = '2025-07-28' // Data escolhida pelo usuário
  
  console.log('=== DEBUG TIMEZONE ===')
  console.log('Data escolhida:', testDate)
  console.log('Timezone local:', Intl.DateTimeFormat().resolvedOptions().timeZone)
  console.log('Offset local (minutos):', new Date().getTimezoneOffset())
  
  const startOfDay = getStartOfDay(testDate)
  const endOfDay = getEndOfDay(testDate)
  
  console.log('Início do dia local:', startOfDay.toString())
  console.log('Final do dia local:', endOfDay.toString())
  
  console.log('Início do dia UTC:', formatDateForAPI(startOfDay))
  console.log('Final do dia UTC:', formatDateForAPI(endOfDay))
  console.log('======================')
}

/**
 * Funções para formatação de datas na UI (sempre no timezone do usuário)
 */

/**
 * Helper para converter string/Date para Date object preservando timezone local
 * @param date Date object ou string
 * @returns Date object interpretado como local
 */
const parseDateForUI = (date: Date | string): Date => {
  if (typeof date === 'string') {
    // Para strings de data como "2025-07-28", força interpretação como local
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // String no formato YYYY-MM-DD, adiciona horário local para não interpretar como UTC
      return new Date(date + 'T00:00:00')
    } else {
      // String ISO completa, mantém comportamento normal
      return new Date(date)
    }
  }
  return date
}

/**
 * Formata data para exibição na UI no timezone local
 * @param date Date object ou string ISO
 * @param format Formato de saída
 * @returns String formatada no timezone local
 */
export const formatDateForUI = (date: Date | string, format: 'short' | 'medium' | 'long' = 'short'): string => {
  const dateObj = parseDateForUI(date)
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  
  switch (format) {
    case 'short':
      options.day = '2-digit'
      options.month = '2-digit'
      options.year = 'numeric'
      return dateObj.toLocaleDateString('pt-BR', options)
    
    case 'medium':
      options.weekday = 'short'
      options.day = '2-digit'
      options.month = 'short'
      options.year = 'numeric'
      return dateObj.toLocaleDateString('pt-BR', options)
    
    case 'long':
      options.weekday = 'long'
      options.day = '2-digit'
      options.month = 'long'
      options.year = 'numeric'
      return dateObj.toLocaleDateString('pt-BR', options)
    
    default:
      return dateObj.toLocaleDateString('pt-BR', options)
  }
}

/**
 * Formata hora para exibição na UI no timezone local
 * @param date Date object ou string ISO
 * @returns String formatada no timezone local (HH:mm)
 */
export const formatTimeForUI = (date: Date | string): string => {
  const dateObj = parseDateForUI(date)
  
  return dateObj.toLocaleTimeString('pt-BR', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Formata data e hora para exibição na UI no timezone local
 * @param date Date object ou string ISO
 * @returns String formatada no timezone local (dd/mm/yyyy HH:mm)
 */
export const formatDateTimeForUI = (date: Date | string): string => {
  const dateObj = parseDateForUI(date)
  
  return dateObj.toLocaleString('pt-BR', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Converte string ISO para Date object preservando timezone local para UI
 * @param isoString String ISO da API
 * @returns Date object para uso na UI
 */
export const parseAPIDateForUI = (isoString: string): Date => {
  return new Date(isoString)
}

/**
 * Formata data para input de data (YYYY-MM-DD) no timezone local
 * @param date Date object ou string ISO
 * @returns String no formato YYYY-MM-DD
 */
export const formatDateInputForUI = (date: Date | string): string => {
  const dateObj = parseDateForUI(date)

  // Usa valores locais para não ter conversão de timezone
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Dias ancorados no fuso do negócio (America/Sao_Paulo)
 *
 * Campos de "dia" do backend (data da ocorrência de aula, competência) são
 * `TIMESTAMPTZ` ancorados na meia-noite de São Paulo — `2026-08-05` está
 * gravado como `2026-08-05T03:00:00Z`. O dia a que esse instante pertence é o
 * dia em São Paulo, não no fuso do browser nem em UTC.
 *
 * Filtrar com o dia do browser quebra para quem acessa de outro fuso; filtrar
 * com o dia em UTC funciona por acidente enquanto o valor for exatamente
 * meia-noite, e passa a errar assim que carregar hora de verdade. As funções
 * abaixo fixam o fuso do negócio nos dois lados.
 *
 * Quando o fuso virar configuração por Arena/CT, é aqui que ele entra.
 */

/** Offset fixo de America/Sao_Paulo (UTC-3). O Brasil não usa mais horário de verão. */
const BUSINESS_UTC_OFFSET_HOURS = 3

interface CalendarParts {
  year: number
  month: number
  day: number
}

/**
 * Extrai o dia em São Paulo de um valor vindo da API.
 *
 * Aceita tanto `YYYY-MM-DD` puro quanto instante ISO. No segundo caso o
 * instante é convertido para o fuso do negócio antes de virar dia — é o que
 * garante que `2026-08-05T03:00:00Z` seja lido como 05/08, e não 04/08.
 */
const businessPartsFromAPI = (value: Date | string): CalendarParts => {
  if (typeof value === 'string') {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
    if (dateOnly) {
      return { year: Number(dateOnly[1]), month: Number(dateOnly[2]) - 1, day: Number(dateOnly[3]) }
    }
  }

  const dateObj = value instanceof Date ? value : new Date(value)
  const shifted = new Date(dateObj.getTime() - BUSINESS_UTC_OFFSET_HOURS * 60 * 60 * 1000)

  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth(), day: shifted.getUTCDate() }
}

/**
 * Extrai o dia de calendário de uma data local (a que o usuário selecionou na tela).
 */
const calendarPartsFromLocal = (date: Date): CalendarParts => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate()
})

/**
 * Monta a janela de um dia para filtro na API, ancorada no fuso do negócio.
 *
 * O dia 05/08 vira `2026-08-05T03:00:00Z` a `2026-08-06T02:59:59Z` — a
 * meia-noite de São Paulo até o último segundo antes da próxima. Não use
 * `getStartOfDay`/`getEndOfDay` para isso: elas usam o fuso do browser, o que
 * desloca a janela para quem acessa de fora do Brasil.
 *
 * @param date Data selecionada na interface
 * @returns `startDate` e `endDate` no formato ISO aceito pela API
 */
export const getBusinessDayRangeForAPI = (date: Date): { startDate: string; endDate: string } => {
  const { year, month, day } = calendarPartsFromLocal(date)
  const startUtcMs = Date.UTC(year, month, day, BUSINESS_UTC_OFFSET_HOURS, 0, 0)

  return {
    startDate: formatDateForAPI(new Date(startUtcMs)),
    endDate: formatDateForAPI(new Date(startUtcMs + 24 * 60 * 60 * 1000 - 1000))
  }
}

/**
 * Formata um dia vindo da API para exibição, no fuso do negócio.
 *
 * @param value Valor de dia da API (`2026-08-05T03:00:00Z` ou `2026-08-05`)
 * @param format Mesmos formatos de `formatDateForUI`
 */
export const formatBusinessDayForUI = (
  value: Date | string,
  format: 'short' | 'medium' | 'long' = 'short'
): string => {
  const { year, month, day } = businessPartsFromAPI(value)
  // Reconstrói como meia-noite UTC e formata em UTC: os componentes já são os
  // do dia em São Paulo, então qualquer nova conversão só reintroduziria erro.
  const dateObj = new Date(Date.UTC(year, month, day))

  const options: Intl.DateTimeFormatOptions = { timeZone: 'UTC' }

  switch (format) {
    case 'medium':
      options.weekday = 'short'
      options.day = '2-digit'
      options.month = 'short'
      options.year = 'numeric'
      break

    case 'long':
      options.weekday = 'long'
      options.day = '2-digit'
      options.month = 'long'
      options.year = 'numeric'
      break

    default:
      options.day = '2-digit'
      options.month = '2-digit'
      options.year = 'numeric'
  }

  return dateObj.toLocaleDateString('pt-BR', options)
}

/**
 * Compara um dia da API (no fuso do negócio) com a data selecionada na interface.
 *
 * @param value Valor de dia da API
 * @param localDate Data selecionada na interface
 */
export const isSameBusinessDay = (value: Date | string, localDate: Date): boolean => {
  const apiParts = businessPartsFromAPI(value)
  const localParts = calendarPartsFromLocal(localDate)

  return (
    apiParts.year === localParts.year &&
    apiParts.month === localParts.month &&
    apiParts.day === localParts.day
  )
}

/**
 * Resolves the "Data do pagamento" of a manual payment for the API.
 *
 * Date inputs give `YYYY-MM-DD`; parsing that directly lands on UTC midnight
 * (21:00 of the PREVIOUS day in São Paulo), which would file the payment
 * under the wrong day in the receipts timeline. So: today keeps the real
 * instant (now); a past day is anchored at MIDDAY in the business timezone,
 * safely inside the chosen day whatever the viewer's timezone.
 *
 * @param dateInput Value of the date input (`YYYY-MM-DD`)
 * @returns ISO string for the API `paidAt` field
 */
export const manualPaymentPaidAtForAPI = (dateInput: string): string => {
  const today = formatDateInputForUI(new Date())
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((dateInput || '').trim())

  if (!match || dateInput === today) {
    return formatDateForAPI(new Date())
  }

  const middayUtcMs = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12 + BUSINESS_UTC_OFFSET_HOURS,
    0,
    0
  )
  return formatDateForAPI(new Date(middayUtcMs))
}

/**
 * Obtém o dia da semana de uma data no formato DayOfWeek enum (1-7, Domingo a Sábado)
 * @param date Date object
 * @returns DayOfWeek enum value (1=Domingo, 2=Segunda, ..., 7=Sábado)
 */
export const getDayOfWeek = (date: Date): DayOfWeek => {
  // JavaScript getDay(): 0=Dom, 1=Seg, 2=Ter, 3=Qua, 4=Qui, 5=Sex, 6=Sab
  // DayOfWeek enum: 1=Dom, 2=Seg, 3=Ter, 4=Qua, 5=Qui, 6=Sex, 7=Sab
  const jsDay = date.getDay()
  return jsDay === 0 ? DayOfWeek.SUNDAY : (jsDay + 1) as DayOfWeek
}

/**
 * Converte JavaScript getDay() para DayOfWeek enum
 * @param jsDay JavaScript day (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns DayOfWeek enum value
 */
export const jsDayToDayOfWeek = (jsDay: number): DayOfWeek => {
  return jsDay === 0 ? DayOfWeek.SUNDAY : (jsDay + 1) as DayOfWeek
}

/**
 * Obtém o nome do dia da semana em português
 * @param dayOfWeek DayOfWeek enum value
 * @param format 'short' para abreviação (ex: 'SEG') ou 'long' para nome completo (ex: 'Segunda-feira')
 * @returns Nome do dia da semana
 */
export const getDayOfWeekName = (dayOfWeek: DayOfWeek, format: 'short' | 'long' = 'long'): string => {
  const names = {
    [DayOfWeek.SUNDAY]: { short: 'DOM', long: 'Domingo' },
    [DayOfWeek.MONDAY]: { short: 'SEG', long: 'Segunda-feira' },
    [DayOfWeek.TUESDAY]: { short: 'TER', long: 'Terça-feira' },
    [DayOfWeek.WEDNESDAY]: { short: 'QUA', long: 'Quarta-feira' },
    [DayOfWeek.THURSDAY]: { short: 'QUI', long: 'Quinta-feira' },
    [DayOfWeek.FRIDAY]: { short: 'SEX', long: 'Sexta-feira' },
    [DayOfWeek.SATURDAY]: { short: 'SAB', long: 'Sábado' }
  }

  return names[dayOfWeek][format]
}

/**
 * Converte minutos desde meia-noite para formato de hora HH:mm
 * @param minutes Minutos desde meia-noite (0-1439)
 * @returns String formatada no formato HH:mm (ex: "09:30", "14:00")
 * @example
 * formatTimeFromMinutes(570) // "09:30"
 * formatTimeFromMinutes(840) // "14:00"
 * formatTimeFromMinutes(0)   // "00:00"
 * formatTimeFromMinutes(1439) // "23:59"
 */
export const formatTimeFromMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Formata yearMonth (YYYY-MM) para exibição no formato "Mês Ano"
 * @param yearMonth String no formato YYYY-MM
 * @returns String formatada com mês por extenso e ano (ex: "março de 2026")
 * @example
 * formatYearMonth("2026-03") // "março de 2026"
 * formatYearMonth("2025-12") // "dezembro de 2025"
 */
export const formatYearMonth = (yearMonth: string): string => {
  const [year, month] = yearMonth.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1)
  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long'
  })
}